import * as React from "react";
import { CollectionFile } from "shared/types/files";
import { useSelector } from "react-redux";
import { getRoutinesLoading } from "selectors/commonSelectors";
import { ADD_COLLECTION_ASSETS_FROM_FEED_ROUTINE } from "constants/ActionTypes";
import { Button } from "@/components/ui/button";
import { SplitButton } from "@/components/ui/split-button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ReduxRootState } from "types/redux-store";

export function SelectedMediaFiles(props: {
  limit: number;
  selectedMediaFiles: CollectionFile[];
  onRemove(file: CollectionFile): void;
  onClear(): void;
  onSubmit(): void;
  onBulkPosts?(): void;
  onBulkStories?(): void;
  onSkip?(): void;
  onCancel?(): void;
  title?: string;
  buttonTitle?: string;
  showBulkActions?: boolean;
  enforceCarouselLimit?: boolean;
  disabled: boolean;
  renderActionButtons?(files: CollectionFile[]): React.ReactNode;
}): JSX.Element {
  const {
    buttonTitle = "Create post",
    showBulkActions = true,
    enforceCarouselLimit = true,
    selectedMediaFiles,
    onClear,
    onSubmit,
    onBulkPosts,
    onBulkStories,
    onCancel,
    onSkip,
    disabled,
  } = props;

  const isUploadingImages = useSelector((state: ReduxRootState) =>
    getRoutinesLoading(state, [ADD_COLLECTION_ASSETS_FROM_FEED_ROUTINE])
  );

  const selectedCount = selectedMediaFiles.length;
  const bulkMenuItems = onBulkStories
    ? [
        {
          key: "bulk-stories",
          label: "Bulk Stories",
          onSelect: () => onBulkStories(),
          disabled: isUploadingImages,
        },
      ]
    : [];

  // Button state logic per Phase 5 requirements
  const cancelEnabled = true;
  const bulkPostsEnabled = selectedCount >= 2;
  const bulkDisabled = !bulkPostsEnabled || isUploadingImages;
  const primaryActionEnabled = enforceCarouselLimit
    ? // Post mode: enabled for skip (0) and valid carousel range (1-10)
      selectedCount === 0 || (selectedCount >= 1 && selectedCount <= 10)
    : // Select-only mode: require at least one selection
      selectedCount > 0;

  return (
    <TooltipProvider>
      <div className="w-full">
        <div className="flex items-center justify-between">
          {/* Left side: Cancel button */}
          <Button
            variant="outline"
            size="md"
            onClick={onCancel || onClear}
            disabled={!cancelEnabled}
            className="h-8 border-neutral-100"
          >
            Cancel
          </Button>

          {/* Right side: Skip link, Bulk posts split button, Create post */}
          <div className="flex items-center gap-3">
            {onSkip && (
              <Button
                variant="ghost"
                size="md"
                onClick={onSkip}
                className="h-8 text-neutral-700 hover:text-neutral-900"
              >
                Skip
              </Button>
            )}

            {/* Bulk posts split button with tooltip when disabled */}
            {showBulkActions && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <SplitButton
                      variant="outline"
                      size="sm"
                      disabled={bulkDisabled}
                      leftLabel="Bulk posts"
                      onLeftClick={() => (onBulkPosts || onSubmit)?.()}
                      menuTriggerAriaLabel="More bulk options"
                      menuItems={bulkMenuItems}
                    />
                  </div>
                </TooltipTrigger>
                {!bulkPostsEnabled && (
                  <TooltipContent>
                    <p>Select 2 or more assets to use bulk create options</p>
                  </TooltipContent>
                )}
              </Tooltip>
            )}

            {/* Create post button with tooltip when disabled due to too many assets */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Button
                    variant="blue-primary"
                    size="md"
                    onClick={selectedCount === 0 && onSkip ? onSkip : onSubmit}
                    disabled={
                      !primaryActionEnabled || disabled || isUploadingImages
                    }
                    className="h-8"
                  >
                    {buttonTitle}
                  </Button>
                </div>
              </TooltipTrigger>
              {enforceCarouselLimit && selectedCount > 10 && (
                <TooltipContent>
                  <p>
                    You can only create carousels with up to 10 items at a time.
                    Use 'Bulk posts' to create individual posts from your
                    selection.
                  </p>
                </TooltipContent>
              )}
            </Tooltip>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
